import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { UserFormValues, Supervisor } from "./types";
import { RotateCcw } from "lucide-react";

interface UserFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<UserFormValues>;
  onSubmit: (values: UserFormValues) => void;
  isEditing: boolean;
  showPassword: boolean;
  setShowPassword: (show: boolean) => void;
  companies: string[];
  supervisors: Supervisor[];
  watchedRole: string;
  handleCompanySelectChange: (value: string) => void;
  onResetPassword?: () => void;
  resetPasswordLoading?: boolean;
}

export const UserFormDialog = ({
  open,
  onOpenChange,
  form,
  onSubmit,
  isEditing,
  showPassword,
  setShowPassword,
  companies,
  supervisors,
  watchedRole,
  handleCompanySelectChange,
  onResetPassword,
  resetPasswordLoading
}: UserFormDialogProps) => {
  const shouldShowUnitHeadCheckbox = watchedRole === "Director" || watchedRole === "VP" || watchedRole === "President" || watchedRole === "CEO";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-secondary border border-primary/20 text-primary-light max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-xl text-primary">
            {isEditing ? "Edit User" : "Add New User"}
          </DialogTitle>
          <DialogDescription className="text-primary-light">
            {isEditing ? "Modify user details and access permissions" : "Create a new user and configure their access permissions"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-primary-light">Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        disabled={isEditing}
                        className="border-gray-800 text-black bg-white"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="full_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-primary-light">Full Name</FormLabel>
                    <FormControl>
                      <Input
                        className="border-gray-800 text-black bg-white"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {!isEditing && (
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-primary-light">Password</FormLabel>
                    <div className="relative">
                      <FormControl>
                        <Input
                          type={showPassword ? "text" : "password"}
                          className="border-gray-800 text-black bg-white pr-10"
                          {...field}
                        />
                      </FormControl>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 text-primary hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </Button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="company_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-primary-light">Company Name</FormLabel>
                    <FormControl>
                      <Select onValueChange={handleCompanySelectChange} value={field.value}>
                        <SelectTrigger className="border-gray-800 w-[255px] text-black bg-white">
                          <SelectValue placeholder="Select Company" />
                        </SelectTrigger>
                        <SelectContent>
                          {companies.map((company) => (
                            <SelectItem key={company} value={company}>
                              {company}
                            </SelectItem>
                          ))}
                          <SelectItem value="add_new_company" className="text-primary font-medium">
                            Add New Company
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-primary-light">Role</FormLabel>
                    <FormControl>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger className="border-gray-800 w-[255px] text-black bg-white">
                          <SelectValue placeholder="Select Role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CEO">CEO</SelectItem>
                          <SelectItem value="President">President</SelectItem>
                          <SelectItem value="VP">VP</SelectItem>
                          <SelectItem value="Director">Director</SelectItem>
                          <SelectItem value="Manager">Manager</SelectItem>
                          <SelectItem value="Team Lead">Team Lead</SelectItem>
                          <SelectItem value="Team member">Team member</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            

            {watchedRole && (watchedRole === "Director" || watchedRole === "VP" || watchedRole === "President" || watchedRole === "Manager" || watchedRole === "Team Lead" || watchedRole === "Team member") && (
              <FormField
                control={form.control}
                name="supervisor_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-primary-light">Supervisor Name</FormLabel>
                    <FormControl>
                      <Select onValueChange={field.onChange} value={field.value || undefined}>
                        <SelectTrigger className="border-gray-800 w-[255px] text-black bg-white">
                          <SelectValue placeholder="Select Supervisor" />
                        </SelectTrigger>
                        <SelectContent>
                          {supervisors.length === 0 ? (
                            <SelectItem value="no-supervisors" disabled>
                              No supervisors available
                            </SelectItem>
                          ) : (
                            supervisors.map((supervisor) => (
                              <SelectItem key={supervisor.id} value={supervisor.id}>
                                {supervisor.full_name} ({supervisor.role})
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {shouldShowUnitHeadCheckbox && (
              <FormField
                control={form.control}
                name="is_unit_head"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-primary/20 p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-primary-light cursor-pointer">
                        Unit Head
                      </FormLabel>
                      <p className="text-sm text-muted-foreground">
                        This person is a unit head and has additional responsibilities
                      </p>
                    </div>
                  </FormItem>
                )}
              />
            )}

            <div className="border border-primary/20 rounded-md p-1 bg-black/20">
              <h3 className="mb-2 font-medium text-primary">Grant Access To</h3>
              <div className="grid grid-cols-2 gap-2">
                <FormField
                  control={form.control}
                  name="access_administration"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border border-primary/10 p-1.5">
                      <div className="space-y-0">
                        <FormLabel className="text-primary-light">Administration Module</FormLabel>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="access_hr"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border border-primary/10 p-1.5">
                      <div className="space-y-0">
                        <FormLabel className="text-primary-light">HR Module</FormLabel>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="access_sales"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border border-primary/10 p-1.5">
                      <div className="space-y-0">
                        <FormLabel className="text-primary-light">Sales Module</FormLabel>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="access_marketing"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border border-primary/10 p-1.5">
                      <div className="space-y-0">
                        <FormLabel className="text-primary-light">Marketing Module</FormLabel>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="access_finance"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border border-primary/10 p-1.5">
                      <div className="space-y-0">
                        <FormLabel className="text-primary-light">Finance Module</FormLabel>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="access_projects"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border border-primary/10 p-1.5">
                      <div className="space-y-0">
                        <FormLabel className="text-primary-light">Projects Module</FormLabel>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="access_legal"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border border-primary/10 p-1.5">
                      <div className="space-y-0">
                        <FormLabel className="text-primary-light">Legal Module</FormLabel>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="access_performance"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border border-primary/10 p-1.5">
                      <div className="space-y-0">
                        <FormLabel className="text-primary-light">Performance Module</FormLabel>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <DialogFooter className="flex justify-between">
              <DialogClose asChild>
                <Button type="button" variant="outline" className="border-primary/20 bg-white text-black hover:bg-transparent hover:text-primary">
                  Cancel
                </Button>
              </DialogClose>
              <div className="flex gap-2">
            {isEditing && (
              <Button 
                type="button" 
                className="bg-primary hover:bg-primary/90 text-black"
                onClick={onResetPassword}
                disabled={resetPasswordLoading || !onResetPassword}
              >
                {resetPasswordLoading ? (
                  <>Resetting...</>
                ) : (
                  <>Reset Password</>
                )}
              </Button>
            )}
                <Button type="submit" className="bg-primary hover:bg-primary/90 text-black">
                  {isEditing ? "Update User" : "Create User"}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
